import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Clock4, MapPin, BarChart3, Newspaper, FileText, Award, BadgeDollarSign, Flame } from "lucide-react";
import SEO from "@/components/SEO";
import LeadDialog from "@/components/LeadDialog";
import HeroImg from "@/components/HeroImg";
import { RateSnapshotWidget } from "@/components/RateSnapshotWidget";
import { CALCULATOR_LIST } from "@/lib/calculators";
import { RESEARCH_CLUSTERS, STATE_OVERVIEW_LINK, PILLAR_GUIDES } from "@/lib/research-clusters";
import { websiteSchema, organizationSchema, faqSchema } from "@/lib/schema";
import { getAllMetros, getMetroCalcPricing, NATIONAL_BASELINES } from "@/lib/metroPricing";
import { US_STATES, getStateByCode } from "@/lib/states";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Resize Unsplash/Pexels hero URLs down to card width (cards render ~300px).
const cdnCardSrc = (url, w) => {
  const u = new URL(url);
  u.searchParams.set("w", String(w));
  if (u.hostname.includes("pexels")) {
    u.searchParams.set("h", String(Math.round(w * 0.75)));
    u.searchParams.set("dpr", "1");
    u.searchParams.set("fit", "crop");
  } else {
    u.searchParams.set("q", "70");
  }
  return u.toString();
};

// Fallback list — shown when there's no analytics traffic yet (cold start) or
// when the trending-metros API is unreachable. Hand-curated to showcase the
// breadth of project types across coastal/inland metros.
const FALLBACK_METROS = [
  { label: "Los Angeles, CA", slug: "los-angeles-california", price: "$11.8K – $26.2K", note: "Bathroom remodel" },
  { label: "Manhattan, NY", slug: "manhattan-new-york", price: "$45.5K – $109.3K", note: "Kitchen remodel" },
  { label: "Austin, TX", slug: "austin-texas", price: "$18.4K – $33.9K", note: "Solar (8 kW)" },
  { label: "Seattle, WA", slug: "seattle-metro-washington", price: "$8.7K – $20.5K", note: "Window replacement (10)" },
  { label: "Miami-Dade, FL", slug: "miami-dade-florida", price: "$14.2K – $32.5K", note: "Roof replacement" },
];

// Hand-curated solar guides — surfaced on the homepage rail to give the four
// newly-written state-specific solar guides immediate distribution while live
// click data warms up. Each entry links to its `/guides/{slug}` page and shows
// the typical GROSS 8 kW system price (federal 25D expired Dec 31, 2025).
const CURATED_SOLAR_GUIDES = [
  { label: "Nevada — 8 kW solar", slug: "solar-panel-cost-nevada-2026", price: "$13.2K – $19.8K", note: "Gross cost · NV Energy net billing" },
  { label: "New Jersey — 8 kW solar", slug: "solar-panel-cost-new-jersey-2026", price: "$18.4K – $25.6K", note: "Gross cost · SREC-II income" },
  { label: "Colorado — 8 kW solar", slug: "solar-panel-cost-colorado-2026", price: "$15.2K – $22.4K", note: "Gross cost · Xcel net metering" },
  { label: "Massachusetts — 8 kW solar", slug: "solar-panel-cost-massachusetts-2026", price: "$19.2K – $26.4K", note: "Gross cost · MA $1K state credit + SMART" },
];

// Curated ROI-playbook guides — surfaced on the homepage rail to give the
// 2026 Cost vs Value ROI cluster (12 guides) immediate distribution. Hub
// page is pinned first; the Power Trio calculator second; then the 3 elite
// projects. Each entry shows the headline cost + recoup percentage.
const CURATED_ROI_GUIDES = [
  { label: "Best ROI Renovations 2026 — Ranked", slug: "best-roi-renovations-2026-ranked", price: "All 16 projects", note: "Interactive plan builder · 9 linked guides" },
  { label: "Curb Appeal Power Trio", slug: "curb-appeal-power-trio-2026", price: "$20.3K · 96%", note: "Bundle calculator · 3 elite projects" },
  { label: "Garage door replacement", slug: "garage-door-replacement-roi-2026", price: "$5.4K · 102%", note: "#1 ROI — only project recouping over cost" },
  { label: "Steel entry door", slug: "steel-entry-door-replacement-roi-2026", price: "$2.5K · 88%", note: "Highest ROI per dollar" },
];

function fmtUSD(v) {
  return v >= 1000
    ? `$${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}K`
    : `$${v}`;
}

/**
 * Resolve trending-metro rows from the backend analytics payload into UI rows.
 * Each backend row is `{metro: slug, calculator: slug, count: N}`. We look up
 * the metro in `getAllMetros()` and derive per-calculator pricing via
 * `getMetroCalcPricing` + project label from `NATIONAL_BASELINES`. Any row
 * that can't be resolved (unknown slug, unknown calculator) is dropped.
 */
function resolveTrending(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const allMetros = getAllMetros();
  const out = [];
  for (const row of rows) {
    const metro = allMetros.find((m) => m.slug === row.metro);
    if (!metro) continue;
    const calcSlug = row.calculator && NATIONAL_BASELINES[row.calculator]
      ? row.calculator
      : "bathroom"; // safe default — every metro has a baseline for bathroom
    const baseline = NATIONAL_BASELINES[calcSlug];
    const pricing = getMetroCalcPricing(metro, calcSlug);
    if (!baseline || !pricing) continue;
    out.push({
      label: `${metro.displayCity}, ${metro.stateCode}`,
      slug: metro.slug,
      price: `${fmtUSD(pricing.low)} – ${fmtUSD(pricing.high)}`,
      note: baseline.label,
    });
  }
  return out;
}

export default function HomePage() {
  const [leadOpen, setLeadOpen] = useState(false);
  const [trending, setTrending] = useState(null); // null = loading, [] = empty, [...] = ready
  const [trendingSource, setTrendingSource] = useState("fallback"); // "live" | "fallback"
  // Geo-personalized headline + chip. Falls back to the original copy if geo
  // fails or returns a non-US IP. Pure client-side: never affects SSR meta /
  // crawlers, so we don't risk Google indexing 50 different "in {state}"
  // variants.
  //
  // Shape: { code: "CA", name: "California", source: "geo" | "manual" }
  // heroState is null while geo is loading OR the visitor is non-US.
  const [heroState, setHeroState] = useState(null);
  const [statePickerOpen, setStatePickerOpen] = useState(false);

  // Fetch geo state once on mount — same endpoint the RateSnapshotWidget uses.
  // Both calls share the 24h backend cache, so the second one is free.
  // If the visitor has manually picked a state in a previous session, that
  // choice wins over geo (respects user intent, e.g., a traveller in a hotel).
  useEffect(() => {
    let cancelled = false;
    // 1. Prefer manual override from localStorage
    try {
      const raw = localStorage.getItem("hcg_state");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.code && parsed?.name) {
          setHeroState({ code: parsed.code, name: parsed.name, source: "manual" });
          return; // skip geo — user already picked
        }
      }
    } catch {
      // ignore malformed storage
    }
    // 2. Fall back to server geo
    (async () => {
      try {
        const r = await fetch(`${API}/geo/state`);
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled && data?.state && data?.state_name) {
          setHeroState({ code: data.state, name: data.state_name, source: "geo" });
          // Persist geo detection too, so every calculator page can pre-fill
          // without threading the state through every internal href.
          try {
            localStorage.setItem(
              "hcg_state",
              JSON.stringify({ code: data.state, name: data.state_name })
            );
          } catch {
            // ignore quota errors
          }
        }
      } catch {
        // silent fail — headline stays at default
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleStatePick = (code) => {
    const s = getStateByCode(code);
    if (!s) return;
    const next = { code: s.code, name: s.name, source: "manual" };
    setHeroState(next);
    setStatePickerOpen(false);
    try {
      localStorage.setItem("hcg_state", JSON.stringify({ code: s.code, name: s.name }));
    } catch {
      // ignore quota errors
    }
  };

  // Fetch trending metros once on mount. Replace the FALLBACK_METROS list with
  // live data only when we get ≥3 resolvable rows back — anything less and the
  // hand-curated fallback shows a stronger, more diverse mix.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/analytics/trending-metros?days=30&limit=5`);
        if (!res.ok) throw new Error("trending fetch failed");
        const data = await res.json();
        const resolved = resolveTrending(data.metros || []);
        if (cancelled) return;
        if (resolved.length >= 3) {
          setTrending(resolved.slice(0, 5));
          setTrendingSource("live");
        } else {
          setTrending(FALLBACK_METROS);
          setTrendingSource("fallback");
        }
      } catch {
        if (!cancelled) {
          setTrending(FALLBACK_METROS);
          setTrendingSource("fallback");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const metrosToShow = trending || FALLBACK_METROS;
  const isLive = trendingSource === "live" && trending && trending.length >= 3;

  // Home-page FAQ schema — these 6 questions are tuned to be the exact
  // queries AI assistants (ChatGPT, Perplexity, Claude.ai, Google AI
  // Overviews) match when a user asks a generic "how much does X cost"
  // question. Each answer is sourced and citation-friendly, so an AI
  // assistant can extract the answer verbatim + cite havencostguide.com.
  const HOME_FAQS = [
    {
      q: "How much does a typical home renovation cost in 2026?",
      a: "Costs vary significantly by project and state. National 2026 mid-range averages: bathroom remodel $14,000–$32,000; kitchen remodel $25,000–$60,000; whole-home flooring $8,000–$24,000; roof replacement $11,000–$22,000; residential solar (8 kW) $20,000–$32,000 gross (the federal 25D credit expired Dec 31, 2025); whole-home window replacement (10 windows) $14,000–$28,000. State-level pricing varies 30–55% above or below these national midpoints based on labor rates, material delivery, and permit fees.",
    },
    {
      q: "Are the HavenCostGuide calculators free to use?",
      a: "Yes, all 17 cost calculators on HavenCostGuide are free, require no signup, and do not require sharing contact information to view your estimate. We do not sell leads to contractors and do not take contractor commissions.",
    },
    {
      q: "How accurate is HavenCostGuide compared to a contractor bid?",
      a: "HavenCostGuide produces baseline cost ranges accurate within ±15% of typical contractor bids for the same scope. Final quotes vary based on home age (pre-1985 homes need 10–15% discovery contingency), material selection, local labor availability, and the specific contractor's overhead. We recommend using HavenCostGuide to validate the bid range BEFORE collecting contractor quotes, then comparing 3 contractor bids against our range.",
    },
    {
      q: "Where does HavenCostGuide's pricing data come from?",
      a: "Our cost baselines are built from the U.S. Bureau of Labor Statistics (regional labor and material rates), the 2026 Remodeling Magazine Cost vs Value Report, HomeAdvisor True Cost Guides, and direct manufacturer MSRPs. State cost indexes adjust the national baseline using a composite of BLS regional wage data, material delivery costs, and permit-fee patterns. Data is refreshed twice yearly (spring and fall).",
    },
    {
      q: "Which renovation projects have the best return on investment in 2026?",
      a: "Per the 2026 Cost vs Value Report, the highest-ROI projects are: garage door replacement (102% recoup), steel entry door replacement (88%), manufactured stone veneer (84%), minor kitchen remodel (82%), and vinyl siding replacement (80%). Major kitchen remodels and bathroom additions typically recoup 60–70%. See HavenCostGuide's ROI ranking guide at /guides/best-roi-renovations-2026-ranked for the full data.",
    },
    {
      q: "Does HavenCostGuide cover every U.S. state?",
      a: "Yes — all 50 U.S. states. Every calculator adjusts pricing using a state-specific cost index, and 260+ deep-dive guides at /guides/{project}-cost-{state}-2026 provide per-state 4-year cost trajectories, top metros, and local cost drivers. Additionally, our atlas pages aggregate all 50 states into sortable comparison tables — see /flooring-cost-by-state for the flagship atlas.",
    },
  ];

  return (
    <>
      <SEO
        title="HavenCostGuide | Free Home Renovation Cost Calculators — All 50 US States, 2026"
        description="Free, independent home renovation cost estimator for all 50 US states. State-adjusted ranges for 17 project types, built from BLS + Remodeling Magazine + HomeAdvisor data."
        path="/"
        structuredData={[websiteSchema(), organizationSchema(), faqSchema(HOME_FAQS)]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-100 bg-white" data-testid="home-hero">
        <div className="absolute inset-0 bg-grain opacity-60" aria-hidden />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:py-28 lg:px-8">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600">
              <span className="h-1.5 w-1.5 rounded-full bg-green-600" />
              Updated Spring 2026 · 50 states covered
            </span>
            <h1 className="mt-5 font-display text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl" data-testid="hero-headline">
              Find out what your home project will <span className="text-green-700">really</span> cost
              {heroState && (
                <span className="text-slate-900"> in <span className="text-green-700">{heroState.name}</span></span>
              )}
              .
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-600">
              Free estimates tailored to your state and project size — built from publicly
              available contractor data and industry benchmarks.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to={
                  heroState
                    ? `/calculators?state=${heroState.code}&utm_source=home&utm_medium=hero-cta&utm_campaign=start-estimate`
                    : "/calculators?utm_source=home&utm_medium=hero-cta&utm_campaign=start-estimate"
                }
                className="group inline-flex h-14 items-center justify-center gap-2 rounded-full bg-green-600 px-8 text-base font-semibold text-white shadow-lg shadow-green-600/20 transition hover:bg-green-700 hover:shadow-xl hover:shadow-green-600/30 sm:text-lg"
                data-testid="hero-start-estimate-button"
              >
                Start a free estimate
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Button
                variant="outline"
                onClick={() => setLeadOpen(true)}
                className="h-14 rounded-full border-2 border-slate-300 px-7 text-base font-semibold transition hover:border-slate-900 hover:bg-slate-50 sm:text-lg"
                data-testid="hero-get-quotes-button"
              >
                Get free quotes
              </Button>
            </div>
            {/* 2026-02 — Geo-personalization chip. Renders only when
                /api/geo/state returns a US state; otherwise gracefully hides.
                Reinforces "your estimate is already state-adjusted" so the
                CTA click feels personal from second one. Click "change" to
                override with a manual pick (persists to localStorage). */}
            {heroState && (
              <div className="mt-4" data-testid="hero-state-chip-wrapper">
                <div
                  className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm shadow-sm"
                  data-testid="hero-state-chip"
                >
                  <MapPin className="h-3.5 w-3.5 text-emerald-700" aria-hidden />
                  <span className="text-slate-700">Estimating for</span>
                  <span className="font-semibold text-emerald-800" data-testid="hero-state-chip-name">
                    {heroState.name}
                  </span>
                  <span className="text-slate-400" aria-hidden>·</span>
                  <button
                    type="button"
                    onClick={() => setStatePickerOpen((v) => !v)}
                    className="text-xs font-medium text-emerald-700 underline-offset-2 hover:text-emerald-900 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-1 rounded"
                    aria-expanded={statePickerOpen}
                    aria-controls="hero-state-picker"
                    data-testid="hero-state-chip-change-button"
                  >
                    not you? change →
                  </button>
                </div>
                {statePickerOpen && (
                  <div
                    id="hero-state-picker"
                    role="group"
                    aria-label="Pick your state"
                    className="mt-2 max-w-md rounded-xl border border-slate-200 bg-white p-3 shadow-md"
                    data-testid="hero-state-picker"
                  >
                    <label htmlFor="hero-state-select" className="mb-1.5 block text-xs font-medium text-slate-600">
                      Your state
                    </label>
                    <select
                      id="hero-state-select"
                      value={heroState.code}
                      onChange={(e) => handleStatePick(e.target.value)}
                      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                      data-testid="hero-state-picker-select"
                    >
                      {US_STATES.map((s) => (
                        <option key={s.code} value={s.code}>{s.name}</option>
                      ))}
                    </select>
                    <p className="mt-2 text-[11px] text-slate-500">
                      We'll remember this on this device.
                    </p>
                  </div>
                )}
              </div>
            )}
            <p className="mt-3 text-xs text-slate-500">
              No signup required · 17 project calculators · 50-state pricing
            </p>

            {/* 2026-02 — Hero-level ROI Showdown surface. The branded
                comparison hub deserves visibility on the homepage above the
                fold, not buried in a side-rail. Subtle treatment so it
                doesn't compete with the primary CTA. */}
            <div
              className="mt-5 flex flex-wrap items-center gap-2 text-sm"
              data-testid="hero-roi-showdown-rail"
            >
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-700 ring-1 ring-emerald-200">
                New
              </span>
              <span className="text-slate-600">Already deciding between projects?</span>
              <Link
                to="/roi-showdown"
                className="inline-flex items-center gap-1 font-semibold text-emerald-700 underline-offset-4 hover:text-emerald-800 hover:underline"
                data-testid="hero-roi-showdown-link"
              >
                Browse 13 ROI head-to-head matchups
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 border-t border-slate-100 pt-6">
              <Stat icon={MapPin} value="50 states" label="Regional pricing" />
              <Stat icon={BarChart3} value="±15%" label="Median accuracy" />
              <Stat icon={Clock4} value="< 60 sec" label="Time to estimate" />
            </div>
          </div>

          <div className="relative hidden lg:block">
            <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-slate-200 shadow-lg">
              {/* Source only matches desktop; mobile (image hidden) gets a 1px placeholder, no download. */}
              <picture>
                <source
                  media="(min-width: 1024px)"
                  srcSet="https://images.unsplash.com/photo-1649083048597-d7b4f1e8a386?crop=entropy&cs=srgb&auto=format,compress&w=480&q=65&fit=crop&ixlib=rb-4.1.0 480w, https://images.unsplash.com/photo-1649083048597-d7b4f1e8a386?crop=entropy&cs=srgb&auto=format,compress&w=750&q=68&fit=crop&ixlib=rb-4.1.0 750w, https://images.unsplash.com/photo-1649083048597-d7b4f1e8a386?crop=entropy&cs=srgb&auto=format,compress&w=1200&q=70&fit=crop&ixlib=rb-4.1.0 1200w"
                  sizes="580px"
                />
                <img
                  src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                  alt="Bright renovated kitchen with white island, wood floors, and warm brass pendant lights"
                  width="800"
                  height="1000"
                  className="h-full w-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                  decoding="sync"
                  data-testid="hero-kitchen-image"
                />
              </picture>
            </div>
            <div className="absolute -bottom-6 -left-6 w-64 rounded-xl border border-slate-200 bg-white p-4 shadow-lg" data-testid="hero-card">
              <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                <ShieldCheck className="h-3.5 w-3.5 text-green-600" />
                Sample · Bathroom · CA
              </div>
              <p className="mt-2 font-display text-2xl font-semibold text-slate-900 tabular-nums">
                $11,200 – $24,500
              </p>
              <p className="mt-1 text-xs text-slate-500">Mid-range, medium bathroom</p>
            </div>
            {/* Rate-snapshot widget — auto-detects the visitor's state via
                /api/geo/state and shows today's renovation loan rates for
                that state. Top-of-funnel financing-intent capture: routes
                browsers who care about cost AND payment into the full
                calculator with their state pre-filled. */}
            <div className="absolute -top-6 -right-6 w-72">
              <RateSnapshotWidget />
            </div>
          </div>
        </div>
      </section>

      {/* Calculator grid */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-testid="calculator-grid-section">
        <div className="mb-12 max-w-2xl">
          <p className="text-xs font-medium uppercase tracking-wider text-green-700">Calculators</p>
          <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            Pick a project. We'll handle the math.
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Each calculator walks you through 4 quick questions and returns a state-adjusted
            cost breakdown in seconds.
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {CALCULATOR_LIST.map((c) => (
            <Link
              key={c.slug}
              to={c.path}
              className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all hover:-translate-y-1 hover:border-slate-300 hover:shadow-lg"
              data-testid={`calculator-card-${c.slug}`}
            >
              <div className="aspect-[4/3] w-full overflow-hidden bg-slate-100">
                {/* 2026-10-01 pass 92y — Local hero images render via HeroImg
                    (AVIF/WebP/mobile variants from optimize-heroes.mjs).
                    The 4 heaviest cards (deck/basement/hardscape/landscaping,
                    previously 0.4-1MB Pinterest/PNG originals) now pull ~35-110KB
                    mobile variants. External CDN images (Unsplash/Pexels with
                    built-in compression params) keep the plain <img>. */}
                {c.image.startsWith("/") ? (
                  <HeroImg
                    src={c.image}
                    alt={c.name}
                    width={800}
                    height={600}
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 300px"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <img
                    src={cdnCardSrc(c.image, 400)}
                    srcSet={`${cdnCardSrc(c.image, 400)} 400w, ${cdnCardSrc(c.image, 640)} 640w, ${cdnCardSrc(c.image, 800)} 800w`}
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 300px"
                    width={800}
                    height={600}
                    alt={c.name}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                  />
                )}
              </div>
              <div className="flex flex-1 flex-col p-5">
                <h3 className="font-display text-lg font-semibold text-slate-900">{c.name}</h3>
                <p className="mt-2 flex-1 text-sm text-slate-600">{c.description}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-green-700">
                  Start estimate
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Storm & Disaster cluster — surface the recovery hub + decision
          calculator high in the home flow because storm-damage traffic is
          time-sensitive (every hour after a named event is a conversion
          opportunity). */}
      <section className="border-y border-amber-200 bg-gradient-to-br from-amber-50/60 via-white to-emerald-50/30" data-testid="home-storm-cluster">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-amber-800">Just hit by hail, hurricane, tornado, wildfire, or flood?</p>
          <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            Storm & Disaster Recovery Center
          </h2>
          <p className="mt-3 max-w-3xl text-base leading-relaxed text-slate-700">
            11 free editorial guides + a free claim-decision calculator. ACV vs RCV math, contractor scam patterns, the 48-hour documentation window, and state-specific insurance realities — written for someone whose roof is currently tarped.
          </p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Link
              to="/storm-damage-claim-calculator"
              className="group flex items-center gap-4 rounded-2xl border-2 border-emerald-600 bg-white p-5 transition hover:shadow-md"
              data-testid="home-storm-calc-link"
            >
              <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-emerald-700 text-2xl">📊</span>
              <div className="flex-1">
                <p className="font-display text-lg font-semibold text-slate-900 group-hover:text-emerald-800">Should I file my claim? →</p>
                <p className="mt-1 text-sm text-slate-600">Free decision tool — your deductible, policy type, roof age. Get a FILE/BORDERLINE/DON'T FILE verdict in 60 seconds.</p>
              </div>
            </Link>
            <Link
              to="/storm-recovery"
              className="group flex items-center gap-4 rounded-2xl border-2 border-amber-500 bg-white p-5 transition hover:shadow-md"
              data-testid="home-storm-recovery-link"
            >
              <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-amber-600 text-2xl">🛠️</span>
              <div className="flex-1">
                <p className="font-display text-lg font-semibold text-slate-900 group-hover:text-amber-800">Storm Recovery Center →</p>
                <p className="mt-1 text-sm text-slate-600">11 disaster guides + 12 high-risk state calculators. Hub for press, FEMA, and homeowners mid-claim.</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Renovate-or-move decision tool — surface BEFORE the bundle
          estimators because for many visitors the question "should I do this
          at all" precedes "how big should it be". Single-calc featured
          block (not the full /decision-tools hub) keeps the home funnel
          focused. */}
      <section className="border-b border-indigo-100 bg-gradient-to-br from-indigo-50/50 via-white to-slate-50/40" data-testid="home-renovate-or-move-cluster">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-800">Should you renovate this house or sell?</p>
          <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            The biggest renovation question — answered in 30 seconds
          </h2>
          <p className="mt-3 max-w-3xl text-base leading-relaxed text-slate-700">
            Free 11-input 5/10/20-year cost-of-ownership math: locked-rate vs prevailing rate, capital-gains exemption, property-tax basis reset, ~12% selling + buying friction. The only US calculator that asks the three inputs that decide the answer in 2026.
          </p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Link
              to="/renovate-or-move-calculator"
              className="group flex items-center gap-4 rounded-2xl border-2 border-indigo-600 bg-white p-5 transition hover:shadow-md"
              data-testid="home-renovate-or-move-calc-link"
            >
              <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-indigo-700 text-2xl">🔁</span>
              <div className="flex-1">
                <p className="font-display text-lg font-semibold text-slate-900 group-hover:text-indigo-800">Renovate or move? →</p>
                <p className="mt-1 text-sm text-slate-600">Free 5/10/20-yr decision math. RENOVATE / MOVE / TOO CLOSE TO CALL verdict with dollar delta. No email gate.</p>
              </div>
            </Link>
            <Link
              to="/guides/when-home-renovations-dont-make-financial-sense"
              className="group flex items-center gap-4 rounded-2xl border-2 border-slate-300 bg-white p-5 transition hover:shadow-md"
              data-testid="home-renovate-or-move-guide-link"
            >
              <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-slate-700 text-2xl">📖</span>
              <div className="flex-1">
                <p className="font-display text-lg font-semibold text-slate-900 group-hover:text-slate-700">When renovations DON&rsquo;T make sense →</p>
                <p className="mt-1 text-sm text-slate-600">The 5-scenario framework + 2026 math behind the calculator. 10-min read.</p>
              </div>
            </Link>
          </div>
          <p className="mt-4 text-xs text-slate-500">
            One of <Link to="/decision-tools" className="font-semibold text-indigo-700 hover:text-indigo-800" data-testid="home-renovate-or-move-hub-link">14 free 2026 decision calculators →</Link>
          </p>
        </div>
      </section>

      {/* Bundle estimators — surface the two whole-project wizards as
          higher-ticket alternatives to the single-calc flow. Indoor first
          (≈$80K-$200K spend cluster), outdoor second (≈$50K-$150K). */}
      <section className="border-y border-slate-100 bg-gradient-to-br from-emerald-50/40 via-white to-slate-50/40" data-testid="home-bundle-estimators">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Renovating multiple rooms?</p>
            <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              Skip the single-calc shuffle.
            </h2>
            <p className="mt-3 text-base text-slate-600">
              Two 3-minute wizards that bundle your whole indoor or outdoor renovation into a single state-adjusted estimate. Best for whole-home remodels and full-backyard makeovers.
            </p>
          </div>

          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <Link
              to="/indoor-renovation-estimator"
              data-testid="home-indoor-estimator-card"
              className="group flex flex-col justify-between rounded-2xl border border-slate-200 bg-white p-6 transition hover:-translate-y-1 hover:border-emerald-300 hover:shadow-lg"
            >
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">
                  Indoor bundle
                </span>
                <h3 className="mt-4 font-display text-2xl font-semibold text-slate-900">
                  Whole Indoor Renovation Estimator
                </h3>
                <p className="mt-2 text-sm text-slate-600">
                  Kitchen + bathroom(s) + flooring + basement, bundled. Bath count toggle built in.
                  Typical full-indoor remodel: <strong>$80K–$200K</strong>.
                </p>
              </div>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 group-hover:text-emerald-800">
                Start indoor estimate
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>

            <Link
              to="/outdoor-renovation-estimator"
              data-testid="home-outdoor-estimator-card"
              className="group flex flex-col justify-between rounded-2xl border border-slate-200 bg-white p-6 transition hover:-translate-y-1 hover:border-emerald-300 hover:shadow-lg"
            >
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-emerald-700 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">
                  Outdoor bundle
                </span>
                <h3 className="mt-4 font-display text-2xl font-semibold text-slate-900">
                  Whole Outdoor Renovation Estimator
                </h3>
                <p className="mt-2 text-sm text-slate-600">
                  Deck + outdoor kitchen + pool + fence + hardscape + landscaping, bundled. Typical
                  full-backyard makeover: <strong>$50K–$150K</strong>.
                </p>
              </div>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 group-hover:text-emerald-800">
                Start outdoor estimate
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Browse by city — homeowner-facing entry to the /metro-pricing hub.
          Placed directly after the calculator grid so visitors who don't pick
          a calculator (because they want to compare cities first) get a clear
          second path. SEO benefit: surfaces the hub link with descriptive
          anchor text from the highest-equity page on the site. */}
      <section
        className="border-y border-slate-100 bg-slate-50/60"
        data-testid="home-metro-pricing-callout"
        aria-labelledby="home-metro-pricing-heading"
      >
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-[1.2fr_1fr]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-green-700">
                Compare by city
              </p>
              <h2
                id="home-metro-pricing-heading"
                className="mt-2 font-display text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl"
              >
                What does renovation cost in your city?
              </h2>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600">
                Side-by-side 2026 pricing for kitchen, bathroom, roofing, windows, solar, and 6 more projects across <strong className="font-semibold text-slate-900">199 U.S. metros</strong>. Search any city, see the typical range, then jump into the calculator.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/metro-pricing"
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-slate-900 px-7 text-base font-semibold text-white transition hover:bg-slate-800"
                  data-testid="home-metro-pricing-cta"
                >
                  Browse 199 metros
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/metro-pricing#state-CA"
                  className="inline-flex h-12 items-center justify-center rounded-full border border-slate-300 bg-white px-6 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:text-slate-900"
                  data-testid="home-metro-pricing-cta-ca"
                >
                  Jump to California
                </Link>
              </div>

              {/* Left-column fillers: balance the long right-column data stack
                  with two compact value-add blocks instead of stock-photo decor.
                  (a) Trending-now horizontal rail — top 3 metros, live data; framed
                  as a "what's hot" surface vs the structured popular-metros list
                  on the right. (b) 4-cluster pod — entry into the new
                  programmatic-SEO sister clusters (fair-price / timeline /
                  licensing / deposit-cap), compounds topical-authority. */}
              <div
                className="mt-10 rounded-2xl border border-slate-200 bg-white p-5"
                data-testid="home-metro-trending-rail"
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Trending right now
                  </p>
                  {isLive && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 ring-1 ring-amber-200"
                      data-testid="home-metro-trending-rail-live-badge"
                    >
                      <Flame className="h-3 w-3" />
                      Live · 30d
                    </span>
                  )}
                </div>
                <p className="mt-1.5 text-xs text-slate-500">
                  Top {Math.min(3, metrosToShow.length)} metros by visitor clicks {isLive ? "this month" : "(typical)"}.
                </p>
                <div
                  className="mt-3 flex flex-wrap gap-2"
                  data-testid="home-metro-trending-rail-pills"
                >
                  {metrosToShow.slice(0, 3).map((m, idx) => (
                    <Link
                      key={m.slug}
                      to={`/metro-pricing#${m.slug}`}
                      className="group inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-amber-300 hover:bg-amber-50 hover:text-amber-900"
                      data-testid={`home-metro-trending-rail-pill-${m.slug}`}
                    >
                      <span className="font-mono text-[10px] tabular-nums text-slate-400 group-hover:text-amber-600">
                        #{idx + 1}
                      </span>
                      <MapPin className="h-3 w-3" />
                      {m.label}
                    </Link>
                  ))}
                </div>
              </div>

              <div
                className="mt-4 rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5"
                data-testid="home-metro-cluster-pod"
              >
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  For any city — 5 sister tools
                </p>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-700">
                  Pricing is one lens. Run the other four before you sign:
                </p>
                <div
                  className="mt-3 grid grid-cols-2 gap-2"
                  data-testid="home-metro-cluster-pod-grid"
                >
                  <Link
                    to="/quote-fairness-calculator"
                    className="group flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50/30 px-3 py-2 text-xs font-semibold text-rose-900 transition hover:bg-rose-50"
                    data-testid="home-metro-cluster-pod-fair-price"
                  >
                    <BadgeDollarSign className="h-3.5 w-3.5 shrink-0 text-rose-700" />
                    <span className="leading-tight">Fair price check</span>
                  </Link>
                  <Link
                    to="/renovation-timeline"
                    className="group flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50/30 px-3 py-2 text-xs font-semibold text-sky-900 transition hover:bg-sky-50"
                    data-testid="home-metro-cluster-pod-timeline"
                  >
                    <Clock4 className="h-3.5 w-3.5 shrink-0 text-sky-700" />
                    <span className="leading-tight">Project timeline</span>
                  </Link>
                  <Link
                    to="/contractor-licensing"
                    className="group flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/30 px-3 py-2 text-xs font-semibold text-emerald-900 transition hover:bg-emerald-50"
                    data-testid="home-metro-cluster-pod-licensing"
                  >
                    <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-emerald-700" />
                    <span className="leading-tight">Contractor licensing</span>
                  </Link>
                  <Link
                    to="/contractor-deposit-rules"
                    className="group flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/30 px-3 py-2 text-xs font-semibold text-amber-900 transition hover:bg-amber-50"
                    data-testid="home-metro-cluster-pod-deposit"
                  >
                    <BadgeDollarSign className="h-3.5 w-3.5 shrink-0 text-amber-700" />
                    <span className="leading-tight">Deposit cap rules</span>
                  </Link>
                  <Link
                    to="/energy-rebates"
                    className="group col-span-2 flex items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50/30 px-3 py-2 text-xs font-semibold text-yellow-900 transition hover:bg-yellow-50"
                    data-testid="home-metro-cluster-pod-energy"
                  >
                    <Flame className="h-3.5 w-3.5 shrink-0 text-yellow-700" />
                    <span className="leading-tight">Energy rebates &amp; state credits</span>
                  </Link>
                </div>
                <p className="mt-3 text-[11px] text-slate-500">
                  Each tool answers a different state-aware question for the same project.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {isLive ? "Trending metros" : "Popular metros"}
                  </p>
                  {isLive && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 ring-1 ring-amber-200"
                      data-testid="home-trending-metros-live-badge"
                      title="Live data: top 5 metros clicked from estimate-results pages in the last 30 days"
                    >
                      <Flame className="h-3 w-3" />
                      Live · 30d
                    </span>
                  )}
                </div>
                <ul
                  className="mt-4 space-y-2 text-sm"
                  data-testid={`home-popular-metros-list-${trendingSource}`}
                >
                  {metrosToShow.map((m) => (
                    <li key={m.slug}>
                      <Link
                        to={`/metro-pricing#${m.slug}`}
                        className="group flex items-center justify-between gap-3 rounded-lg px-3 py-2 transition hover:bg-slate-50"
                        data-testid={`home-popular-metro-${m.slug}`}
                      >
                        <span className="flex flex-col">
                          <span className="font-medium text-slate-900">{m.label}</span>
                          <span className="text-xs text-slate-500">{m.note}</span>
                        </span>
                        <span className="font-mono text-xs tabular-nums text-slate-700 group-hover:text-green-700">
                          {m.price}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/metro-pricing"
                  className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-green-700 hover:text-green-800"
                  data-testid="home-popular-metros-see-all"
                >
                  See all 199 metros
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <Link
                  to="/photos/state-window-trends"
                  className="ml-4 mt-4 inline-flex items-center gap-1 text-sm font-medium text-rose-700 hover:text-rose-800"
                  data-testid="home-gallery-link"
                  title="Pinterest-ready 50-state window replacement photo gallery"
                >
                  Photo gallery →
                </Link>

                {/* Quiet contractor recruitment funnel. Lower-emphasis text,
                    explicit "for contractors" framing so homeowners scroll past
                    while contractors actively seeking a lead network notice it. */}
                <div className="mt-4 border-t border-slate-100 pt-4" data-testid="home-for-contractors-callout">
                  <Link
                    to="/for-contractors"
                    className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 hover:underline"
                    data-testid="home-for-contractors-link"
                  >
                    Are you a contractor? Apply to receive leads →
                  </Link>
                </div>

                {/* Featured solar guides — hand-curated 2026 state guides, surfaced
                    here for immediate distribution to the 4 newest articles. */}
                <div
                  className="mt-6 border-t border-slate-200 pt-5"
                  data-testid="home-featured-solar-section"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      New: 2026 solar guides
                    </p>
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 ring-1 ring-amber-200"
                      title="Hand-curated 2026 state-by-state solar guides"
                    >
                      Fresh · May 2026
                    </span>
                  </div>
                  <ul
                    className="mt-3 space-y-2 text-sm"
                    data-testid="home-featured-solar-list"
                  >
                    {CURATED_SOLAR_GUIDES.map((g) => (
                      <li key={g.slug}>
                        <Link
                          to={`/guides/${g.slug}`}
                          className="group flex items-center justify-between gap-3 rounded-lg px-3 py-2 transition hover:bg-amber-50/40"
                          data-testid={`home-featured-solar-${g.slug}`}
                        >
                          <span className="flex flex-col">
                            <span className="font-medium text-slate-900">{g.label}</span>
                            <span className="text-xs text-slate-500">{g.note}</span>
                          </span>
                          <span className="font-mono text-xs tabular-nums text-slate-700 group-hover:text-amber-700">
                            {g.price}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* ROI Playbook rail — the 2026 Cost vs Value ROI cluster.
                    Hub page first (the canonical entry with the plan builder),
                    then the Power Trio calculator, then the 3 top-ROI elite
                    projects. Highest-intent pre-sale traffic gets a clear path. */}
                <div
                  className="mt-6 border-t border-slate-200 pt-5"
                  data-testid="home-featured-roi-section"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      2026 ROI playbook
                    </p>
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 ring-1 ring-emerald-200"
                      title="The 2026 Cost vs Value Report ROI cluster — ranked, with interactive calculators"
                    >
                      Selling? Start here
                    </span>
                  </div>
                  <ul
                    className="mt-3 space-y-2 text-sm"
                    data-testid="home-featured-roi-list"
                  >
                    {CURATED_ROI_GUIDES.map((g) => (
                      <li key={g.slug}>
                        <Link
                          to={`/guides/${g.slug}`}
                          className="group flex items-center justify-between gap-3 rounded-lg px-3 py-2 transition hover:bg-emerald-50/40"
                          data-testid={`home-featured-roi-${g.slug}`}
                        >
                          <span className="flex flex-col">
                            <span className="font-medium text-slate-900">{g.label}</span>
                            <span className="text-xs text-slate-500">{g.note}</span>
                          </span>
                          <span className="font-mono text-xs tabular-nums text-slate-700 group-hover:text-emerald-700">
                            {g.price}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/guides/best-roi-renovations-2026-ranked"
                    className="ml-3 mt-3 inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800"
                    data-testid="home-featured-roi-cta"
                  >
                    Build your renovation plan →
                  </Link>
                  {/* 2026-02 — ROI Showdown cluster surface. The /roi-showdown
                      hub aggregates 13 head-to-head matchups across 4 themed
                      batches; promoting it from the homepage concentrates
                      topical authority for "which renovation has better ROI"
                      queries. */}
                  <Link
                    to="/roi-showdown"
                    className="ml-3 mt-2 inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800"
                    data-testid="home-roi-showdown-cta"
                  >
                    Or compare head-to-head: ROI Showdown (13 matchups) →
                  </Link>
                </div>
                {isLive && (
                  <Link
                    to="/trending"
                    className="ml-4 mt-4 inline-flex items-center gap-1 text-sm font-medium text-amber-700 hover:text-amber-800"
                    data-testid="home-trending-link"
                  >
                    See the full top 25 →
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured research pillars — surfaces the highest-leverage long-form
          guides (cost index by state, solar ROI by state, renovate-or-move
          calculator) that were previously buried in chronological /guides
          listings + footer only. P3 internal-link audit: these pages were
          drifting toward GSC "Discovered — currently not crawled" because
          they had no prominent above-the-fold inbound link from the
          homepage. Driven by PILLAR_GUIDES registry — same list also
          surfaces in the footer. */}
      <section
        className="border-t border-slate-100 bg-gradient-to-b from-emerald-50/60 to-white"
        data-testid="home-pillar-guides"
      >
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
                Featured research
              </p>
              <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
                Big-picture research, free
              </h2>
              <p className="mt-3 max-w-2xl text-base leading-relaxed text-slate-600">
                Three deep, hand-built research pieces that answer the questions you
                can&apos;t get from a calculator alone — comparing across states, weighing
                solar payback, or deciding whether to renovate or move.
              </p>
            </div>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PILLAR_GUIDES.map((g) => (
              <Link
                key={g.slug}
                to={g.path}
                className="group rounded-2xl border border-emerald-200/70 bg-white p-6 transition hover:border-emerald-400 hover:shadow-lg"
                data-testid={`home-pillar-guide-${g.slug}`}
              >
                <div className="text-3xl" aria-hidden="true">{g.emoji}</div>
                <h3 className="mt-3 font-display text-lg font-semibold text-slate-900 group-hover:text-emerald-700">
                  {g.label}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{g.description}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                  Open research →
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Research clusters grid — surfaces the 6 SEO clusters
          (quote fairness / timeline / licensing / deposits / rebates / permit
          cost) + state-overview hub. Previously these pages had no global-nav
          discovery path; this grid sits between trending metros and trust
          band so engaged visitors who scroll past the headline calculators
          can dig into the answer-engine clusters. */}
      <section
        className="border-t border-slate-100 bg-white"
        data-testid="home-research-clusters"
      >
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
                Research tools
              </p>
              <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
                Past the calculator? Six deeper lenses.
              </h2>
              <p className="mt-3 max-w-2xl text-base leading-relaxed text-slate-600">
                Once you have a budget range, these tools answer the next set of questions —
                quote fairness, timeline, licensing, deposits, permits, and rebates. Each one is
                state-aware and free.
              </p>
            </div>
            <Link
              to={STATE_OVERVIEW_LINK.path}
              className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
              data-testid="home-research-state-overview-cta"
            >
              {STATE_OVERVIEW_LINK.emoji} See all six for one state →
            </Link>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {RESEARCH_CLUSTERS.map((c) => (
              <Link
                key={c.slug}
                to={c.path}
                className="group rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-emerald-300 hover:shadow-md"
                data-testid={`home-research-cluster-${c.slug}`}
              >
                <div className="text-2xl" aria-hidden="true">{c.emoji}</div>
                <h3 className="mt-3 font-display text-base font-semibold text-slate-900 group-hover:text-emerald-700">
                  {c.label}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.description}</p>
                <span className="mt-3 inline-flex text-xs font-semibold text-emerald-700 opacity-0 transition group-hover:opacity-100">
                  Open {c.shortLabel.toLowerCase()} →
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trust band */}
      <section className="border-y border-slate-200 bg-slate-50" data-testid="trust-band">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-14 sm:grid-cols-3 sm:px-6 lg:px-8">
          <TrustItem
            title="Built on public data"
            body="We aggregate contractor pricing, BLS labor rates, and industry benchmark reports — all publicly available sources."
          />
          <TrustItem
            title="Updated twice yearly"
            body="Cost ranges are refreshed every 6 months to reflect material price shifts and regional labor changes."
          />
          <TrustItem
            title="Budget guidance, not bids"
            body="Estimates are for budgeting only. Always get 3+ written bids from licensed local contractors."
          />
        </div>
      </section>

      {/* For editors & press — discovery rail for /press, /license, /featured-in.
          Compact, low-key, positioned after the homeowner trust band so it
          doesn't compete with the calculator CTAs above the fold. */}
      <section
        className="border-b border-slate-100 bg-white"
        data-testid="home-for-editors"
        aria-labelledby="home-for-editors-heading"
      >
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-green-700">
                For editors &amp; press
              </p>
              <h2
                id="home-for-editors-heading"
                className="mt-1 font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl"
              >
                Cite, embed, or republish our data.
              </h2>
            </div>
            <p className="max-w-sm text-sm text-slate-600">
              All editorial content is CC-BY-4.0 licensed. Use it freely on your site with attribution — no permission needed.
            </p>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Link
              to="/metro-pricing"
              className="group rounded-2xl border border-slate-200 bg-white p-6 transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-sm"
              data-testid="home-link-metro-pricing"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 text-white">
                <BadgeDollarSign className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-display text-base font-semibold text-slate-900">Metro pricing</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Compare 2026 home renovation costs across 199 U.S. metros — 11 project types each, searchable.
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-green-700 transition group-hover:text-green-800">
                Browse metros
                <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>

            <Link
              to="/press"
              className="group rounded-2xl border border-slate-200 bg-white p-6 transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-sm"
              data-testid="home-link-press"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 text-white">
                <Newspaper className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-display text-base font-semibold text-slate-900">Press kit</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Full 2026 Cost Index methodology, ready-to-cite quotes, downloadable PDF, and interview contact.
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-green-700 transition group-hover:text-green-800">
                Open press kit
                <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>

            <Link
              to="/license"
              className="group rounded-2xl border border-slate-200 bg-white p-6 transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-sm"
              data-testid="home-link-license"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 text-white">
                <FileText className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-display text-base font-semibold text-slate-900">Republish (CC-BY-4.0)</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Drop the full text of any article on your site — free, with one attribution snippet. Copy-paste HTML included.
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-green-700 transition group-hover:text-green-800">
                See terms &amp; snippet
                <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>

            <Link
              to="/featured-in"
              className="group rounded-2xl border border-slate-200 bg-white p-6 transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-sm"
              data-testid="home-link-featured-in"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 text-white">
                <Award className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-display text-base font-semibold text-slate-900">Featured in</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Publications that have republished our research. Self-report your republication and we'll feature you back.
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-green-700 transition group-hover:text-green-800">
                See the wall
                <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-testid="home-final-cta">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-900 p-10 text-white sm:p-14">
          <div className="grid items-center gap-8 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                Ready to talk to a real contractor?
              </h2>
              <p className="mt-4 max-w-xl text-base text-slate-300">
                We'll match you with up to 3 vetted local pros. No spam, no obligation —
                just real bids on your project.
              </p>
            </div>
            <div className="flex justify-start lg:justify-end">
              <Button
                onClick={() => setLeadOpen(true)}
                className="h-12 rounded-full bg-green-600 px-8 text-base font-semibold text-white hover:bg-green-700"
                data-testid="home-cta-get-quotes"
              >
                Get free quotes
              </Button>
            </div>
          </div>
        </div>
      </section>

      <LeadDialog open={leadOpen} onOpenChange={setLeadOpen} source="home" />
    </>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-slate-500">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <p className="mt-1 font-display text-2xl font-semibold text-slate-900">{value}</p>
    </div>
  );
}

function TrustItem({ title, body }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-green-700 ring-1 ring-slate-200">
        <ShieldCheck className="h-4 w-4" />
      </span>
      <div>
        <h4 className="font-display text-base font-semibold text-slate-900">{title}</h4>
        <p className="mt-1 text-sm text-slate-600">{body}</p>
      </div>
    </div>
  );
}
